import { DetectedEvent } from '@ais-ui/websockets';
import React, { useMemo } from 'react';
import { cn, timeSince } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useEventSeverity } from './useEventSeverity';
import {
  AccordionListItem,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncatedText,
} from '@ais-ui/ui-core';
import { PiCheckCircle, PiBoat, PiWarningCircle } from 'react-icons/pi';
import { EventDropDownMenu } from './EventDropdownMenu';
import { EventItemContent } from './EventItemContent';
import { useTimeSinceTick } from './useTimeSinceTick';

interface EventItemProps {
  event: DetectedEvent;
}

export const EventItem = ({ event }: EventItemProps) => {
  const tick = useTimeSinceTick();

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const isActive = useMemo(() => {
    return !event.deactivated;
  }, [event.deactivated]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const timeAgo = useMemo(() => timeSince(event.act_ts), [event.act_ts, tick]);

  const severity = useEventSeverity(event);

  const shipIdentifier = useMemo(() => {
    if (event.shipName) return event.shipName;
    if (event.mmsi && event.mmsi > 0) return `MMSI ${event.mmsi}`;
    if (event.imo) return `IMO ${event.imo}`;
    if (event.callSign) return event.callSign;
    return 'Unknown';
  }, [event.shipName, event.mmsi, event.imo, event.callSign]);

  return (
    <AccordionListItem
      key={event.id}
      value={event.id}
      compact
      className={cn(
        'px-2 border-l-4 transition-all rounded-l-lg',
        isActive ? 'border-l-blue-500' : 'border-l-transparent'
      )}
      actionButtons={
        <>
          {event.ack_state === 1 && (
            <Tooltip>
              <TooltipTrigger>
                <PiWarningCircle className="w-5 h-5 text-yellow-600" />
              </TooltipTrigger>
              <TooltipContent>Not acknowledged</TooltipContent>
            </Tooltip>
          )}
          {event.ack_state === 2 && (
            <Tooltip>
              <TooltipTrigger>
                <PiCheckCircle className="w-5 h-5 text-green-600" />
              </TooltipTrigger>
              <TooltipContent>
                <div>
                  <h3 className="font-medium text-base text-left">
                    Acknowledged
                  </h3>
                  <Separator className="my-2" />
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <p className="text-sm font-medium flex-none">By</p>
                      <p className="text-sm text-muted-foreground text-ellipsis overflow-hidden">
                        {event.ack_state_modified_by}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <p className="text-sm font-medium flex-none">At</p>
                      <p className="text-sm text-muted-foreground text-ellipsis overflow-hidden">
                        {formatDateTimeWithTimeZone(
                          event.ack_state_modified_ts
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </>
      }
      triggerContent={
        <div className="space-y-1 flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <PiBoat className="w-4 h-4 flex-shrink-0" />
            <TruncatedText
              text={`${event.name} - ${shipIdentifier}`}
              className="text-xs text-left font-medium leading-none group-hover:underline"
              maxWidth="160px"
            />
          </div>
          <div className="flex items-center space-x-1.5">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                severity === 'note' && 'bg-blue-500',
                severity === 'warning' && 'bg-yellow-500',
                severity === 'alert' && 'bg-red-500'
              )}
            />
            <p className="text-sm text-left text-muted-foreground">
              {timeAgo}
            </p>
          </div>
        </div>
      }
      kebabMenu={<EventDropDownMenu event={event} />}
    >
      <EventItemContent event={event} />
    </AccordionListItem>
  );
};
